import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "sonner";

// Phase 14 — admin step-up gate. Shown when the session is authenticated as an
// admin but hasn't completed the 6-digit email OTP (user.admin_pending). The
// session keeps normal-user privileges until verified — enforced server-side.
export const AdminOtpPanel = ({ onVerified }) => {
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef();

  useEffect(() => {
    if (cooldown <= 0) return;
    timer.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  const verify = async () => {
    const clean = code.trim();
    if (!/^\d{6}$/.test(clean)) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    try {
      await api.adminOtpVerify(clean);
      toast.success("Admin access unlocked for this session");
      await refresh();
      onVerified?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code — request a new one.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.adminOtpSend();
      setCooldown(60);
      toast.success("A new code is on its way to your inbox");
    } catch (e) {
      if (e?.response?.status === 429) setCooldown(60);
      toast.error(e?.response?.data?.detail || "Could not send a new code");
    }
  };

  return (
    <div data-testid="admin-otp-panel" className="max-w-md mx-auto bg-white border border-amber-200 rounded-2xl p-8 shadow-sm">
      <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
        <ShieldCheck className="w-5 h-5 text-amber-600" />
      </div>
      <h2 className="font-display text-2xl text-forest mt-4 tracking-tight">Verify your admin access</h2>
      <p className="text-sm text-neutral-600 mt-2">
        We emailed you a 6-digit code. Enter it below to unlock admin tools for this
        session — until then your account works as a regular member.
      </p>
      <div className="mt-5 flex items-center gap-2">
        <Input
          data-testid="admin-otp-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && verify()}
          placeholder="123456"
          inputMode="numeric"
          className="h-12 text-center text-xl tracking-[0.4em] font-mono"
        />
        <Button
          data-testid="admin-otp-verify-btn"
          onClick={verify}
          disabled={busy || code.trim().length !== 6}
          className="h-12 rounded-full bg-forest hover:bg-forest-hover text-white px-6"
        >
          {busy ? "Checking…" : "Verify"}
        </Button>
      </div>
      <button
        data-testid="admin-otp-resend-btn"
        onClick={resend}
        disabled={cooldown > 0}
        className="mt-4 text-xs text-forest underline underline-offset-2 disabled:text-neutral-400 disabled:no-underline inline-flex items-center gap-1.5"
      >
        <MailCheck className="w-3.5 h-3.5" />
        {cooldown > 0 ? `Resend available in ${cooldown}s` : "Didn't get it? Resend the code"}
      </button>
      <p className="text-[11px] text-neutral-400 mt-3">
        Codes expire after 10 minutes and work once. Check spam if it hasn't arrived.
      </p>
    </div>
  );
};
